import TextboxWithFilter from "@/components/common/TextboxWithFilter";
import { useQueryParams } from "@/hooks/general/useQueryParams";
import { debounce } from "@/utilize/debounce";
import { ChangeEvent, useMemo } from "react";
import UsersTable from "./UsersTable";
import { TUserParamsType } from "../../types";
import UsersFilter from "./UserFilters";
import { Pagination, Typography } from "@/components/kit";
import { useStoreOrg } from "../../store/useStoreOrganization";
import useDetectFlag from "../../hooks/useDetectFlag";
import NoResult from "./NoResult";
import EmptyStateWithIcon from "@/components/common/EmptyStateWithIcon";
import useGetItAdmins from "@/hooks/query/admins/useGetItAdmins";

export interface IProps {
  isFetching?: boolean;
}

const RenderUsers = () => {
  const { data, isFetching, params, setParams, isFetchingRoles } =
    useGetItAdmins();
  const { file, region, regionDomain, country } = useStoreOrg();

  const countryDetcted = useDetectFlag(country);
  const usersData = data;

  function handleSetPageIndex(page: number) {
    setParams({ ...params, page });
  }

  function onSelectLimit(limit: number) {
    setParams({ ...params, page: 1, limit });
  }
  const {
    page,
    limit,
    orderBy,
    orderDirection,
    tab,

    ...restParams
  } = params;

  const hasFilter = Object.values(restParams).some(
    (value) => value !== undefined,
  );
  const renderContent = useMemo(() => {
    const devicesList = usersData?.items;
    const isEmpty = !devicesList || devicesList?.length === 0;

    const renderWithFilter = (children: React.ReactNode) => (
      <WithFilter>{children}</WithFilter>
    );
    if (hasFilter && isEmpty && !isFetching && !isFetchingRoles) {
      return renderWithFilter(<NoResult />);
    }
    if (!hasFilter && isEmpty && !isFetching && !isFetchingRoles) {
      return <EmptyStateWithIcon title="no user" />;
    }
    return renderWithFilter(
      <div className=" flex flex-col flex-1">
        <div className="flex-1 overflow-y-auto basis-0">
          <UsersTable
            users={usersData?.items || []}
            isLoading={isFetching || isFetchingRoles}
            filter={{
              orderBy: params.orderBy,
              orderDirection: params.orderDirection,
            }}
            setFilter={(filter) => setParams({ ...params, ...filter })}
          />
        </div>
        <div className="px-6">
          <Pagination
            totalItems={usersData?.meta?.itemsCount}
            totalPages={usersData?.meta?.totalPages || 0}
            limit={limit}
            activePage={page}
            onSetActivePage={handleSetPageIndex}
            onSelectLimit={onSelectLimit}
          />
        </div>
      </div>,
    );
  }, [hasFilter, usersData, isFetching, isFetchingRoles]);

  return (
    <>
      {file && (
        <div className="px-6">
          <div className="flex border mb-10 border-gray-100 p-3 rounded-md items-center justify-between">
            <div className="flex items-center gap-3">
              <img className="size-8" src={URL.createObjectURL(file)} />
              <div className="space-y-2 flex flex-col ">
                <Typography
                  className="!text-gray-500"
                  variant="Caption2-Regular"
                >
                  Empower Link Alliance 2
                </Typography>
                <p className="text-sm !font-bold">
                  {region}.{regionDomain}.tiro.so
                </p>
              </div>
            </div>
            <div
              className="!text-[24px]"
              style={{ fontFamily: "Twemoji Country Flags" }}
            >
              {countryDetcted?.flag}
            </div>
          </div>
        </div>
      )}

      {renderContent}
    </>
  );
};

const WithFilter = ({ children }: { children: React.ReactNode }) => {
  const { params, setParams, deleteParam } = useQueryParams<TUserParamsType>();

  const debounced = debounce((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setParams({ ...params, page: 1, search: e.target.value });
    } else {
      deleteParam("search");
    }
  });

  return (
    <div className="gap-4 flex flex-col h-full  flex-1">
      <div className="px-6">
        <div className="flex items-center gap-2 ">
          <TextboxWithFilter
            placeholder="Search in name/Email"
            value={params?.search}
            onChange={(e) => {
              debounced(e);
            }}
          />
          {/* {id && <ShowSelectedOnly checked={checked} onChange={onChange} />} */}
        </div>

        <UsersFilter />
      </div>

      {children}
    </div>
  );
};

export default RenderUsers;
